import React, { ReactNode } from "react";
import dayjs from "dayjs";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import Drawer, {
  DrawerFacts,
  DrawerLoading,
  DrawerMeta,
  DrawerSection,
  DrawerText,
} from "Components/UI/Drawer/Drawer";
import { CellLink, EMPTY_VALUE, EntityCell, StatusBadge, formatPersonName } from "Components/UI/DataTable";
import { displayDate } from "Shared/Utils/display";

type Person = { first_name?: string | null; last_name?: string | null };

/** The fields both the dashboard and the Assignments page provide. */
export type AssignmentLike = {
  id: string;
  vehicle?: {
    id?: string;
    name?: string;
    image?: { file?: string | null } | null;
  } | null;
  operator?: Person | string | null;
  start_datetime?: string | null;
  end_datetime?: string | null;
  start_odometer?: number | string | null;
  end_odometer?: number | string | null;
  comment?: string | null;
};

/** "3d 4h", "45m"; "Ongoing" without an end. */
export const formatAssignmentDuration = (
  start?: string | null,
  end?: string | null,
) => {
  if (!start) return EMPTY_VALUE;
  if (!end) return "Ongoing";
  const minutes = dayjs(end).diff(dayjs(start), "minute");
  if (!Number.isFinite(minutes) || minutes < 0) return EMPTY_VALUE;
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts = [days && `${days}d`, hours && `${hours}h`, !days && mins && `${mins}m`];
  return parts.filter(Boolean).join(" ") || "0m";
};

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const miles = (value: number | null) =>
  value === null ? EMPTY_VALUE : `${value.toLocaleString("en-US")} mi`;

const dateTime = (value?: string | null) => displayDate(value, "dateTime");

type Props = {
  open: boolean;
  onClose: () => void;
  assignment: AssignmentLike | null;
  loading?: boolean;
  /** Header icon buttons, e.g. edit. */
  actions?: ReactNode;
  onOpenVehicle?: (id: string) => void;
};

const AssignmentDrawer = ({
  open,
  onClose,
  assignment,
  loading = false,
  actions,
  onOpenVehicle,
}: Props) => {
  const ready = !loading && !!assignment;
  const operator =
    typeof assignment?.operator === "string"
      ? assignment.operator
      : formatPersonName(assignment?.operator);
  const start = toNumber(assignment?.start_odometer);
  const end = toNumber(assignment?.end_odometer);
  const isOngoing = !!assignment?.start_datetime && !assignment?.end_datetime;
  const vehicle = assignment?.vehicle;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={ready ? vehicle?.name || "Assignment" : "Loading…"}
      aria-label="Assignment details"
      eyebrow={
        ready ? (
          <StatusBadge tone={isOngoing ? "success" : "neutral"}>
            {isOngoing ? "In progress" : "Assignment"}
          </StatusBadge>
        ) : null
      }
      subtitle={
        ready && operator ? (
          <DrawerMeta icon={<PersonOutlineIcon />}>{operator}</DrawerMeta>
        ) : null
      }
      actions={ready ? actions : null}
    >
      {!ready ? (
        <DrawerLoading lines={5} />
      ) : (
        <>
          <DrawerSection title="Vehicle">
            <EntityCell
              icon={<LocalShippingOutlinedIcon />}
              image={vehicle?.image?.file}
              title={
                vehicle?.id && onOpenVehicle ? (
                  <CellLink onClick={() => onOpenVehicle(vehicle.id as string)}>
                    {vehicle.name || EMPTY_VALUE}
                  </CellLink>
                ) : (
                  vehicle?.name || EMPTY_VALUE
                )
              }
            />
          </DrawerSection>
          <DrawerSection title="Period">
            <DrawerFacts
              items={[
                { label: "Start", value: dateTime(assignment.start_datetime) },
                { label: "End", value: dateTime(assignment.end_datetime) },
                {
                  label: "Duration",
                  value: formatAssignmentDuration(
                    assignment.start_datetime,
                    assignment.end_datetime,
                  ),
                },
              ]}
            />
          </DrawerSection>
          <DrawerSection title="Odometer">
            <DrawerFacts
              items={[
                { label: "Start", value: miles(start) },
                { label: "End", value: miles(end) },
                {
                  label: "Usage",
                  value: start !== null && end !== null ? miles(end - start) : EMPTY_VALUE,
                },
              ]}
            />
          </DrawerSection>
          {assignment.comment && (
            <DrawerSection title="Comment">
              <DrawerText>{assignment.comment}</DrawerText>
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  );
};

export default AssignmentDrawer;
